import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import CancelIcon from '@mui/icons-material/Cancel';
import ListAltIcon from '@mui/icons-material/ListAlt';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel, GridSortModel, GridToolbar } from '@mui/x-data-grid';

import InternalPropertyLink from '~/components/Accommodation/Pages/Properties/components/InternalPropertyLink';
import MappingLogModal from '~/components/Accommodation/Pages/Properties/components/MappingLogModal';
import AccommodationVerifyMapping from '~/components/Accommodation/components/AccommodationVerifyMapping/AccommodationVerifyMapping';
import { useAccommodationServiceFetch } from '~/components/Accommodation/hooks/useAccommodationServiceFetch';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import useToggleState from '~/hooks/useToggleState';

import { MappedInternalProperty, getPotentialMappings, updateMapping } from '~/services/AccommodationService';

import { AccommodationPropertiesSearchInput } from './AccommodationPropertiesSearchForm';

interface Props {
  searchInput: AccommodationPropertiesSearchInput;
  pageIndex?: number;
  onPageChange: (pageIndex: number) => void;
}

const PAGE_SIZE = 20;

const AUTO_APPROVE_DISTANCE_THRESHOLD = 50;

/** Columns hidden by default; users can show via column visibility menu. */
const DEFAULT_HIDDEN_COLUMNS: Record<string, boolean> = {
  source: false,
  sourceId: false,
  supplierProperties: false,
};

const DEFAULT_SORT_MODEL: GridSortModel = [{ field: 'chainName', sort: 'asc' }];

export default function PotentialMappingsResultsTable({ searchInput, pageIndex, onPageChange }: Props) {
  const [logsId, setLogsId] = useState<string | null>(null);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);
  const [isApproving, setIsApproving] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();

  const {
    isToggled: isMappingLogModalOpen,
    toggleOn: setOpenMappingLogModal,
    toggleOff: setCloseMappingLogModal,
  } = useToggleState(false);

  const showMappingLogs = useCallback(
    async (logsId: string) => {
      setLogsId(logsId);
      setOpenMappingLogModal();
    },
    [setOpenMappingLogModal],
  );

  const getProperties = useCallback(
    () =>
      getPotentialMappings({
        name: searchInput.propertyName,
        id: searchInput.id,
        country: searchInput.country || undefined,
        internalService: searchInput.internalService || undefined,
        chainNames:
          searchInput.chainFilterMode === 'include' && searchInput.chainNames?.length
            ? searchInput.chainNames
            : undefined,
        excludedChainNames:
          searchInput.chainFilterMode === 'exclude' && searchInput.chainNames?.length
            ? searchInput.chainNames
            : undefined,
        page: String((pageIndex ?? 0) + 1),
        limit: String(PAGE_SIZE),
      }),
    [
      pageIndex,
      searchInput.id,
      searchInput.propertyName,
      searchInput.country,
      searchInput.internalService,
      searchInput.chainNames,
      searchInput.chainFilterMode,
    ],
  );

  const { fetchReqState, refetch } = useAccommodationServiceFetch({
    fetchFn: getProperties,
  });

  const handleRejectMapping = useCallback(
    async (propertyId: string, mappedPropertyId: string) => {
      const confirmed = await showConfirmDialog({
        title: 'Reject Mapping?',
        description: 'This will reject the mapping, are you sure you want to proceed?',
      });
      if (!confirmed) return;
      try {
        await updateMapping({ propertyId, mappedPropertyId, verificationStatus: 'Rejected' });
        enqueueSnackbar('Successfully rejected mapping', { variant: 'success' });
        refetch();
      } catch {
        enqueueSnackbar('Failed to reject mapping', { variant: 'error' });
      }
    },
    [showConfirmDialog, enqueueSnackbar, refetch],
  );

  const handleSelectAll = useCallback(() => {
    if (fetchReqState.status === 'succeeded') {
      setSelectionModel(fetchReqState.result.mappedProperties.map((p) => p.id));
    }
  }, [fetchReqState]);

  const handleAutoApprove = useCallback(async () => {
    if (fetchReqState.status !== 'succeeded') {
      return;
    }

    setIsConfirmDialogOpen(false);

    const eligible = selectionModel.filter((id) => {
      const row = fetchReqState.result.mappedProperties.find((p) => p.id === id);
      return row && (row.distance ?? 0) < AUTO_APPROVE_DISTANCE_THRESHOLD;
    });

    const skipped = selectionModel.length - eligible.length;

    setIsApproving(true);
    try {
      const results = await Promise.allSettled(
        eligible.map((id) => {
          const row = fetchReqState.result.mappedProperties.find((p) => p.id === id)!;
          return updateMapping({
            propertyId: row.property.id,
            mappedPropertyId: row.id,
            verificationStatus: 'Approved',
            verifiedComments: 'Mapped by User in Bulk post Automapped from script import-svc-bedbank-properties',
          });
        }),
      );

      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected');

      if (succeeded > 0) {
        enqueueSnackbar(`Approved ${succeeded} mapping(s).`, { variant: 'success' });
      }

      failed.forEach((f) => {
        const message = f.status === 'rejected' && f.reason instanceof Error ? f.reason.message : 'Unknown error';
        enqueueSnackbar(`Failed to approve mapping: ${message}`, { variant: 'error' });
      });

      if (skipped > 0) {
        enqueueSnackbar(`Skipped ${skipped} mapping(s) with distance >= ${AUTO_APPROVE_DISTANCE_THRESHOLD}.`, {
          variant: 'warning',
        });
      }

      refetch();
    } finally {
      setIsApproving(false);
    }
  }, [fetchReqState, selectionModel, refetch, enqueueSnackbar]);

  const columns: Array<GridColDef<MappedInternalProperty>> = [
    {
      field: 'name',
      headerName: 'Accommodation Property Name',
      display: 'flex',
      minWidth: 350,
      flex: 1,
      hideable: false,
      renderCell: (params) => (
        <Link
          href={`/accommodation/properties/${params.row.property.id}`}
          target="_blank"
          className="accommodation-properties-search-result-link"
        >
          {params.row.property.name}
        </Link>
      ),
    },
    {
      field: 'supplier name',
      headerName: 'Supplier Property Name',
      display: 'flex',
      minWidth: 350,
      hideable: true,
      renderCell: (params) => {
        return <Box>{params.row.internalProperty.name}</Box>;
      },
    },
    {
      field: 'country',
      headerName: 'Country',
      minWidth: 150,
      hideable: true,
      renderCell: (params) => <Box>{params.row.property.country}</Box>,
    },
    {
      field: 'chainName',
      headerName: 'Chain Name',
      minWidth: 150,
      flex: 1,
      display: 'flex',
      hideable: true,
      renderCell: (params) => <Box>{params.row.property.chainName || '-'}</Box>,
    },
    {
      field: 'source',
      headerName: 'Source',
      display: 'flex',
      minWidth: 100,
      hideable: true,
      renderCell: (params) => {
        return <Box>{params.row.internalProperty.source}</Box>;
      },
    },
    {
      field: 'sourceId',
      headerName: 'Source ID',
      display: 'flex',
      minWidth: 350,
      hideable: true,
      renderCell: (params) => {
        return (
          <InternalPropertyLink
            source={params.row.internalProperty.source}
            sourceId={params.row.internalProperty.sourceId}
          />
        );
      },
    },
    {
      field: 'supplierProperties',
      headerName: 'Supplier - ID',
      minWidth: 200,
      flex: 1,
      renderCell: (params) => {
        const supplierProperties = params.row.internalProperty.supplierProperties || [];
        return (
          <Box>
            {supplierProperties.map((supplier, index) => (
              <Typography key={index} variant="body2">
                {supplier.supplier} - {supplier.supplierId}
              </Typography>
            ))}
          </Box>
        );
      },
    },
    {
      field: 'nameScore',
      headerName: 'Name Score',
      minWidth: 120,
      hideable: true,
      type: 'number',
      valueGetter: (_value, row) => row.nameScore ?? null,
      renderCell: (params) => <Box>{params.row.nameScore != null ? Number(params.row.nameScore.toFixed(2)) : '-'}</Box>,
    },
    {
      field: 'distance',
      headerName: 'Address Distance',
      minWidth: 140,
      hideable: true,
      type: 'number',
      valueGetter: (_value, row) => row.distance ?? null,
      renderCell: (params) => (
        <Box>{params.row.distance != null ? `${Number(params.row.distance.toFixed(3))} m` : '-'}</Box>
      ),
    },
    {
      field: ' ',
      headerName: 'Actions',
      headerAlign: 'center',
      width: 185,
      sortable: false,
      align: 'center',
      display: 'flex',
      renderCell: (params) => {
        return (
          <Stack direction="row" gap={1}>
            <Tooltip title="Show mapping logs">
              <IconButton onClick={() => showMappingLogs(params.row.internalProperty.id)} color="info">
                <ListAltIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
            <AccommodationVerifyMapping
              propertyId={params.row.property.id}
              mappedPropertyId={params.row.id}
              onComplete={refetch}
            />
            <Tooltip title="Reject mapping">
              <IconButton onClick={() => handleRejectMapping(params.row.property.id, params.row.id)} color="error">
                <CancelIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  return (
    <>
      {fetchReqState.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {JSON.stringify(fetchReqState.error)}
        </Alert>
      )}
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="text" onClick={handleSelectAll}>
          Select All
        </Button>
        <Button
          variant="contained"
          onClick={() => setIsConfirmDialogOpen(true)}
          color="primary"
          disabled={isApproving || selectionModel.length === 0}
        >
          {isApproving ? 'Approving...' : 'Approve'}
        </Button>
      </Stack>
      <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Bulk Approval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to approve {selectionModel.length} selected mapping(s)?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDialogOpen(false)} variant="text">
            Cancel
          </Button>
          <Button onClick={handleAutoApprove} variant="contained" color="primary">
            Approve
          </Button>
        </DialogActions>
      </Dialog>
      <DataGrid
        className="potential-mappings-results-table"
        columns={columns}
        rows={fetchReqState.status === 'succeeded' ? fetchReqState.result.mappedProperties : []}
        rowCount={fetchReqState.status === 'succeeded' ? fetchReqState.result.total : undefined}
        getRowHeight={() => 70}
        pageSizeOptions={[PAGE_SIZE]}
        paginationMode="server"
        paginationModel={{ page: pageIndex, pageSize: PAGE_SIZE }}
        onPaginationModelChange={({ page }) => onPageChange(page)}
        loading={fetchReqState.status === 'pending'}
        checkboxSelection
        onRowSelectionModelChange={(newModel) => setSelectionModel(newModel)}
        rowSelectionModel={selectionModel}
        autoHeight
        disableColumnFilter
        disableDensitySelector
        sortingMode="client"
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        initialState={{
          columns: {
            columnVisibilityModel: DEFAULT_HIDDEN_COLUMNS,
          },
          sorting: {
            sortModel: DEFAULT_SORT_MODEL,
          },
        }}
        slots={{
          toolbar: GridToolbar,
        }}
      />
      {isMappingLogModalOpen && (
        <MappingLogModal
          isMappingLogModalOpen={isMappingLogModalOpen}
          setCloseMappingLogModal={setCloseMappingLogModal}
          logsSourceId={logsId}
          type="property"
        />
      )}
    </>
  );
}
